/**
 * @file Cookie.ts
 * @description Getting, setting and removing cookies with NEXT.JS
 *
 * Can be used on the client side, anywhere.
 * Can be used for server side rendering in getServerSideProps.
 * Can be used in API handlers.
 * Can be used in Next.js 13+.
 *
 * @module Cookie
 */
import { getCookie, getCookies } from 'cookies-next';
import isString from 'lodash/isString';
import { USER_ID, IN_MOBILE_APP, USER_ROLES, OAUTH_TOKEN, LOGGED_IN, ABSMARTLY_ID, } from './constants/cookies';
/**
 * Get all cookies as string
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const cookies = Cookie.getCookiesString();
 *
 * expect(cookies).toBe('cookie1=value1; cookie2=value2');
 *
 *
 * @returns The cookies as a string
 *
 */
export const getCookiesString = (context) => {
    const cookies = getCookies(context);
    return Object.entries(cookies)
        .map(([key, value]) => `${key}=${value}`)
        .join('; ');
};
/**
 * Get the user ID from the cookie
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const userId = Cookie.getUserId();
 *
 * @param context - The context object.
 * @returns The user ID if it exists. Otherwise, undefined.
 *
 */
export const getUserId = (context) => {
    const userId = getCookie(USER_ID, context);
    return isString(userId) ? userId : undefined;
};
/**
 * Get the OAuth token from the cookie
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const oAuthToken = Cookie.getOAuthToken();
 *
 * @param context - The context object.
 * @returns The OAuth token if it exists. Otherwise, undefined.
 *
 */
export const getOAuthToken = (context) => {
    const oAuthToken = getCookie(OAUTH_TOKEN, context);
    return isString(oAuthToken) ? oAuthToken : undefined;
};
/**
 * Get the `is mobile app` from the cookie
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const isMobileApp = Cookie.getIsMobileApp();
 *
 * @param context - The context object.
 * @returns The `is mobile app` if it exists. Otherwise, undefined.
 *
 */
export const getIsMobileApp = (context) => {
    const isMobileApp = getCookie(IN_MOBILE_APP, context) === 'true';
    return isMobileApp;
};
/**
 * Get the user roles from the cookie
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const userRoles = Cookie.getUserRoles();
 *
 * @param context - The context object.
 * @returns The user roles if they exist. Otherwise, an empty array.
 *
 */
export const getUserRoles = (context) => {
    const roles = getCookie(USER_ROLES, context);
    return isString(roles) ? roles.split('&') : [];
};
/**
 * Get the user logged in from the cookie `logged_in`
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const userRoles = Cookie.getUserLoggedIn();
 *
 * @param context - The context object.
 * @returns true if the user logged in cookie is `yes`. Otherwise, false.
 *
 */
export const getIsUserLoggedIn = (context) => {
    const isUserLoggedIn = getCookie(LOGGED_IN, context);
    return isUserLoggedIn === 'yes';
};
/**
 *  Get the AB Smartly ID from the cookie
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const abSmartlyId = Cookie.getABSmartlyID();
 *
 * @param context - The context object.
 * @returns The AB Smartly ID if it exists. Otherwise, undefined.
 *
 */
export const getABSmartlyId = (context) => getCookie(ABSMARTLY_ID, context);
